/**
 * One colour per persona, used everywhere that persona appears. A room with
 * eight voices in it is unreadable if every voice is grey.
 *
 * The palette deliberately avoids red and amber — red means "killed" and amber
 * means a warning, and a persona colour that collides with a state colour is
 * worse than no colour at all.
 *
 * Tailwind purges dynamically-built class names, so these are whole literal
 * strings rather than `text-${hue}-700`.
 */

export interface SeatColor {
  text: string;
  dot: string;
  border: string;
  soft: string;
}

/** The colour of a person, or of anything with no seat to colour it by. */
export const NEUTRAL: SeatColor = {
  text: 'text-gray-800 dark:text-gray-200',
  dot: 'bg-gray-400',
  border: 'border-l-gray-300',
  soft: 'bg-gray-50',
};

export const PALETTE: SeatColor[] = [
  { text: 'text-indigo-700 dark:text-indigo-400', dot: 'bg-indigo-500', border: 'border-l-indigo-400', soft: 'bg-indigo-50' },
  { text: 'text-teal-700 dark:text-teal-400', dot: 'bg-teal-500', border: 'border-l-teal-400', soft: 'bg-teal-50' },
  { text: 'text-fuchsia-700 dark:text-fuchsia-400', dot: 'bg-fuchsia-500', border: 'border-l-fuchsia-400', soft: 'bg-fuchsia-50' },
  { text: 'text-cyan-700 dark:text-cyan-400', dot: 'bg-cyan-500', border: 'border-l-cyan-400', soft: 'bg-cyan-50' },
  { text: 'text-violet-700 dark:text-violet-400', dot: 'bg-violet-500', border: 'border-l-violet-400', soft: 'bg-violet-50' },
  { text: 'text-lime-700 dark:text-lime-400', dot: 'bg-lime-500', border: 'border-l-lime-400', soft: 'bg-lime-50' },
];

/**
 * Colour by ORDER, not by id — ids grow forever as personas are added and
 * removed, so keying off them would eventually give two live personas the same
 * hue.
 */
export function colorsByOrder(ids: number[]): Map<number, SeatColor> {
  const out = new Map<number, SeatColor>();
  ids.forEach((id, n) => out.set(id, PALETTE[n % PALETTE.length]));
  return out;
}
